import { SubscriptionRecurring } from '@affine/graphql';

export interface SubscribeIntent {
  coupon: string | null;
  plan: string;
  recurring: SubscriptionRecurring;
}

export const WORKSPACE_PLANS_SUBSCRIBE_PARAMS = {
  coupon: 'subscribeCoupon',
  plan: 'subscribePlan',
  recurring: 'subscribeRecurring',
} as const;

const productAliases: Record<string, [string, SubscriptionRecurring]> = {
  pro: ['Pro', SubscriptionRecurring.Yearly],
  'monthly-pro': ['Pro', SubscriptionRecurring.Monthly],
  team: ['Team', SubscriptionRecurring.Yearly],
  'monthly-team': ['Team', SubscriptionRecurring.Monthly],
  enterprise: ['Enterprise', SubscriptionRecurring.Yearly],
  'monthly-enterprise': ['Enterprise', SubscriptionRecurring.Monthly],
};

const planAliases: Record<string, string> = {
  enterprise: 'Enterprise',
  pro: 'Pro',
  team: 'Team',
};

const recurringAliases: Record<string, SubscriptionRecurring> = {
  monthly: SubscriptionRecurring.Monthly,
  yearly: SubscriptionRecurring.Yearly,
};

function normalized(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

export function getSubscribeIntentFromSearchParams(
  searchParams: URLSearchParams
): SubscribeIntent {
  let plan = 'Pro';
  let recurring = SubscriptionRecurring.Yearly;

  const productName = normalized(searchParams.get('product'));
  const productAlias = productName ? productAliases[productName] : undefined;
  if (productAlias) {
    [plan, recurring] = productAlias;
  }

  const planName = normalized(searchParams.get('plan'));
  if (planName && planAliases[planName]) {
    plan = planAliases[planName];
  }

  const recurringName = normalized(searchParams.get('recurring'));
  if (recurringName && recurringAliases[recurringName]) {
    recurring = recurringAliases[recurringName];
  }

  return {
    coupon: searchParams.get('coupon'),
    plan,
    recurring,
  };
}

export function buildWorkspacePlansSubscribeUrl(
  workspaceId: string,
  intent: SubscribeIntent
): string {
  const searchParams = new URLSearchParams();
  searchParams.set('tab', 'workspace:plans');
  searchParams.set(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.plan, intent.plan);
  searchParams.set(
    WORKSPACE_PLANS_SUBSCRIBE_PARAMS.recurring,
    intent.recurring
  );
  if (intent.coupon) {
    searchParams.set(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.coupon, intent.coupon);
  }
  return `/workspace/${encodeURIComponent(workspaceId)}/settings?${searchParams.toString()}`;
}

export function getWorkspacePlansSubscribeIntent(
  searchParams: URLSearchParams
): SubscribeIntent | null {
  const plan = searchParams.get(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.plan);
  const recurring = searchParams.get(
    WORKSPACE_PLANS_SUBSCRIBE_PARAMS.recurring
  );

  if (!plan || !recurring) {
    return null;
  }

  if (
    recurring !== SubscriptionRecurring.Monthly &&
    recurring !== SubscriptionRecurring.Yearly
  ) {
    return null;
  }

  return {
    coupon: searchParams.get(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.coupon),
    plan,
    recurring,
  };
}

export function clearWorkspacePlansSubscribeParams(
  searchParams: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.coupon);
  next.delete(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.plan);
  next.delete(WORKSPACE_PLANS_SUBSCRIBE_PARAMS.recurring);
  return next;
}
